import React, { useEffect, useState } from "react";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Link,
    useLocation,
} from "react-router-dom";
import ScoreCard from "./components/ScoreCard";
import DetailedScoreCard from "./components/DetailedScoreCard";
import Loading from "./components/Loading";
import { useResult } from "./hooks/useResult";
import { getScoreCardDetails, QuestionDetail } from "./services/api";

const Result: React.FC = () => {
    useEffect(() => {
        document.title = "Niesbud Scorecard";
    }, []);

    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const token = params.get("token") || "";

    const fullPath = location.pathname.replace(/^\/result\//, "");
    const [id, index] = fullPath.split("/");

    const { data, loading, error } = useResult({ id, token, index });

    // ✅ State for answer sheet modal
    const [questionDetails, setQuestionDetails] = useState<QuestionDetail[]>([]);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [showDetails, setShowDetails] = useState(false);

    // ✅ Handler for "View Answer Sheet" button
    const handleViewAnswers = async () => {
        setLoadingDetails(true);
        try {
            const details = await getScoreCardDetails({ id, token, index });
            setQuestionDetails(details);
            setShowDetails(true);
        } catch (err: any) {
            console.error("Error fetching details:", err);
            alert("Failed to load answer sheet. Please try again.");
        } finally {
            setLoadingDetails(false);
        }
    };

    if (loading) return <Loading />;

    if (error) {
        return (
            <div className="bg-white p-16 rounded-2xl text-center shadow-2xl">
                <h2 className="text-red-500 text-2xl font-bold mb-3">
                    ❌ {error}
                </h2>
                <p className="text-slate-500 mb-5">
                    Result ID: {id} / {token} / {index}
                </p>
                <Link
                    to="/"
                    className="inline-block mt-2 px-6 py-3 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-semibold transition"
                >
                    Go Home
                </Link>
            </div>
        );
    }

    if (!data) return null;

    return (
        <>
            <ScoreCard
                data={data}
                onViewAnswers={handleViewAnswers}
                isLoadingAnswers={loadingDetails}
            />

            {showDetails && (
                <DetailedScoreCard
                    questions={questionDetails}
                    candidateName={data.candidateName}
                    niesbudRegNo={data.niesbudRegNo}
                    onClose={() => setShowDetails(false)}
                />
            )}
        </>
    );
};

const Home: React.FC = () => (
    <div className="bg-white p-10 rounded-2xl shadow-2xl text-center">
        <h1 className="text-slate-700 text-3xl font-bold mb-2">
            Incorrect URL
        </h1>
    </div>
);

const App: React.FC = () => {
    return (
        <Router>
            <div className="max-w-4xl mx-auto p-5">
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/result/*" element={<Result />} />
                </Routes>
            </div>
        </Router>
    );
};

export default App;